import React, {useEffect, useState} from 'react';
import {Save, CheckCircle, Heart, Lightbulb, Users} from 'lucide-react';

/**
 * Middle Stage Self Assessment component with modern UI design
 * Matches the style of LessonPlanHistory and AssessmentManagementPanel
 */
const MiddleStageSelfAssessment = ({
                                       assignmentId,
                                       submission,
                                       onSubmitComplete,
                                       loading,
                                       setLoading
                                   }) => {
    // Define the response options matching FourOptionResponse enum
    const responseOptions = [
        {value: 'Yes', label: 'Yes', emoji: '😊👍'},
        {value: 'To an extent', label: 'To an extent', emoji: '😐👉'},
        {value: 'No', label: 'No', emoji: '😔👎'},
        {value: 'Not sure', label: 'Not sure', emoji: '🤔'}
    ];

    // Define the grid categories and statements mapping to backend enums
    // The id should match the actual enum VALUE (text string) in exact order as backend
    const progressGrid = {
        awareness: [
            {id: 'I was able to learn something new.', text: 'I was able to learn something new.'},
            {
                id: 'I was attentive to every detail of the activity.',
                text: 'I was attentive to every detail of the activity.'
            },
            {id: 'I was able to understand the activity.', text: 'I was able to understand the activity.'},
            {
                id: 'I was able to focus and engage with the activity.',
                text: 'I was able to focus and engage with the activity.'
            },
            {id: 'I was able to follow the instructions.', text: 'I was able to follow the instructions.'},
            {
                id: 'I was able to find purpose and meaning in the activity.',
                text: 'I was able to find purpose and meaning in the activity.'
            }
        ],
        sensitivity: [
            {
                id: 'I was able to understand and express my emotions.',
                text: 'I was able to understand and express my emotions.'
            },
            {
                id: 'I was able to motivate myself & my peer when things were difficult.',
                text: 'I was able to motivate myself & my peer when things were difficult.'
            },
            {
                id: 'I was able to understand the emotions of my peer.',
                text: 'I was able to understand the emotions of my peer.'
            },
            {
                id: 'I was able to seek and use support from my peers and teacher.',
                text: 'I was able to seek and use support from my peers and teacher.'
            },
            {
                id: 'I was able to contribute individually or as a group member.',
                text: 'I was able to contribute individually or as a group member.'
            },
            {id: 'I was able to help others in some way.', text: 'I was able to help others in some way.'}
        ],
        creativity: [
            {
                id: "I was curious to explore and learn new things during the activity.",
                text: "I was curious to explore and learn new things during the activity."
            },
            {
                id: "I was able to think of 'out of the box' solutions.",
                text: "I was able to think of 'out of the box' solutions."
            },
            {
                id: "I was able to think of new ways to do the activity.",
                text: "I was able to think of new ways to do the activity."
            },
            {
                id: "I was able to express my creativity while doing the activity.",
                text: "I was able to express my creativity while doing the activity."
            },
            {id: "I was able to generate innovative ideas.", text: "I was able to generate innovative ideas."},
            {id: "I was able to take calculated risks.", text: "I was able to take calculated risks."}
        ]
    };

    // Initialize form state
    const [form, setForm] = useState({
        // Reflection responses
        proud_of_effort: null,
        apply_to_real_life: null,
        motivated_to_learn_more: null,
        // Grid selections
        awareness_selections: [],
        sensitivity_selections: [],
        creativity_selections: [],
        // Learning reflections
        insights: '',
        most_interesting: '',
        need_practice: '',
        need_help: ''
    });

    // Initialize from existing submission if available
    useEffect(() => {
        if (submission?.self_assessment) {
            try {
                const selfAssessment = submission.self_assessment;
                setForm({
                    proud_of_effort: selfAssessment.self_reflection?.proud_of_effort || null,
                    apply_to_real_life: selfAssessment.self_reflection?.apply_to_real_life || null,
                    motivated_to_learn_more: selfAssessment.self_reflection?.motivated_to_learn_more || null,
                    awareness_selections: selfAssessment.progress_grid?.awareness || [],
                    sensitivity_selections: selfAssessment.progress_grid?.sensitivity || [],
                    creativity_selections: selfAssessment.progress_grid?.creativity || [],
                    insights: selfAssessment.learnings?.insights || '',
                    most_interesting: selfAssessment.learnings?.most_interesting || '',
                    need_practice: selfAssessment.learnings?.need_practice || '',
                    need_help: selfAssessment.learnings?.need_help || ''
                });
            } catch (error) {
                console.error("Error parsing existing self assessment:", error);
            }
        }
    }, [submission]);

    // Handle reflection option selection
    const handleReflectionChange = (questionKey, value) => {
        setForm(prev => ({
            ...prev,
            [questionKey]: value
        }));
    };

    // Handle grid statement selection (toggle)
    const handleGridSelectionChange = (category, itemId) => {
        const selectionKey = `${category}_selections`;
        setForm(prev => {
            const currentSelections = [...prev[selectionKey]];
            const index = currentSelections.indexOf(itemId);

            if (index === -1) {
                currentSelections.push(itemId);
            } else {
                currentSelections.splice(index, 1);
            }

            return {
                ...prev,
                [selectionKey]: currentSelections
            };
        });
    };

    // Handle text input changes
    const handleTextChange = (field, value) => {
        setForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Convert form data to API format that matches SelfAssessmentPackage
    const prepareFormData = () => {
        // Debug: Log the exact values being sent
        console.log('Self Assessment Debug:');
        console.log('Creativity selections:', form.creativity_selections);
        form.creativity_selections.forEach((selection, index) => {
            console.log(`Position ${index}:`, JSON.stringify(selection));
            console.log(`Character codes:`, selection.split('').map(c => c.charCodeAt(0)));
        });

        return {
            self_reflection: {
                proud_of_effort: form.proud_of_effort,
                apply_to_real_life: form.apply_to_real_life,
                motivated_to_learn_more: form.motivated_to_learn_more
            },
            progress_grid: {
                awareness: form.awareness_selections,
                sensitivity: form.sensitivity_selections,
                creativity: form.creativity_selections
            },
            learnings: {
                insights: form.insights.trim(),
                most_interesting: form.most_interesting.trim(),
                need_practice: form.need_practice.trim(),
                need_help: form.need_help.trim()
            }
        };
    };

    // Submit the form
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Import API service
        const {getApiServiceForGrade} = require('../../utils/gradeUtils');
        const currentApiService = getApiServiceForGrade(6); // Assuming middle grade

        if (setLoading) setLoading(true);

        try {
            const formData = prepareFormData();
            console.log('Submitting self assessment data:', formData); // Debug log
            const result = await currentApiService.addSelfAssessment(assignmentId, formData);

            if (onSubmitComplete) {
                onSubmitComplete(result);
            }
        } catch (error) {
            console.error("Error submitting self assessment:", error);
            alert(`Failed to submit: ${error.message}`);
        } finally {
            if (setLoading) setLoading(false);
        }
    };

    // Check if the form has been submitted (properly check for meaningful data)
    const isSubmitted = submission?.self_assessment &&
        Object.keys(submission.self_assessment).length > 0 &&
        submission.self_assessment.self_reflection;

    // Determine if form is disabled (already submitted or loading)
    const isDisabled = isSubmitted || loading;

    return (
        <div className="bg-white font-[poppins] rounded-2xl shadow-md overflow-hidden border border-gray-200 mb-8">
            {/* Header */}
            <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#D67C2C] shadow-lg">
                        <CheckCircle className="w-6 h-6 text-white"/>
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Student's Self Reflection</h2>
                        <p className="text-gray-600 mt-1">Reflect on your learning experience and progress</p>
                    </div>
                </div>
            </div>

            <div className="p-8">
                <form onSubmit={handleSubmit}>
                    {/* Reflection Questions Section */}
                    <div className="mb-8">
                        <div className="bg-blue-50 rounded-xl p-4 mb-6 border border-blue-200">
                            <p className="text-sm font-medium text-blue-800 mb-2">Reflection Questions</p>
                            <p className="text-sm text-blue-700">
                                Based on your experience of the activity, please select the response that best applies to you.
                            </p>
                        </div>

                        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Statement</th>
                                        <th className="px-6 py-4 text-center text-sm font-medium text-gray-900" colSpan="4">Your Response</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {/* Reflection 1 */}
                                    <tr className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-sm text-gray-900 w-2/5">
                                            I am proud of myself and my effort.
                                        </td>
                                        {responseOptions.map((option) => (
                                            <td key={option.value} className="px-4 py-4 text-center">
                                                <div className="flex flex-col items-center">
                                                    <div className={`text-2xl mb-2 ${isDisabled ? 'opacity-50' : ''}`}>
                                                        {option.emoji}
                                                    </div>
                                                    <label className="inline-flex items-center">
                                                        <input
                                                            type="radio"
                                                            name="proud_of_effort"
                                                            value={option.value}
                                                            checked={form.proud_of_effort === option.value}
                                                            onChange={() => handleReflectionChange('proud_of_effort', option.value)}
                                                            className="form-radio h-4 w-4 text-[#D67C2C] focus:ring-[#D67C2C]"
                                                            disabled={isDisabled}
                                                        />
                                                        <span className="ml-2 text-xs font-medium text-gray-700">{option.label}</span>
                                                    </label>
                                                </div>
                                            </td>
                                        ))}
                                    </tr>

                                    {/* Reflection 2 */}
                                    <tr className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-sm text-gray-900">
                                            I will be able to apply what I learnt from this activity to real life situations.
                                        </td>
                                        {responseOptions.map((option) => (
                                            <td key={option.value} className="px-4 py-4 text-center">
                                                <div className="flex flex-col items-center">
                                                    <div className={`text-2xl mb-2 ${isDisabled ? 'opacity-50' : ''}`}>
                                                        {option.emoji}
                                                    </div>
                                                    <label className="inline-flex items-center">
                                                        <input
                                                            type="radio"
                                                            name="apply_to_real_life"
                                                            value={option.value}
                                                            checked={form.apply_to_real_life === option.value}
                                                            onChange={() => handleReflectionChange('apply_to_real_life', option.value)}
                                                            className="form-radio h-4 w-4 text-[#D67C2C] focus:ring-[#D67C2C]"
                                                            disabled={isDisabled}
                                                        />
                                                        <span className="ml-2 text-xs font-medium text-gray-700">{option.label}</span>
                                                    </label>
                                                </div>
                                            </td>
                                        ))}
                                    </tr>

                                    {/* Reflection 3 */}
                                    <tr className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-sm text-gray-900">
                                            I am motivated to learn further about the concepts covered in the activity.
                                        </td>
                                        {responseOptions.map((option) => (
                                            <td key={option.value} className="px-4 py-4 text-center">
                                                <div className="flex flex-col items-center">
                                                    <div className={`text-2xl mb-2 ${isDisabled ? 'opacity-50' : ''}`}>
                                                        {option.emoji}
                                                    </div>
                                                    <label className="inline-flex items-center">
                                                        <input
                                                            type="radio"
                                                            name="motivated_to_learn_more"
                                                            value={option.value}
                                                            checked={form.motivated_to_learn_more === option.value}
                                                            onChange={() => handleReflectionChange('motivated_to_learn_more', option.value)}
                                                            className="form-radio h-4 w-4 text-[#D67C2C] focus:ring-[#D67C2C]"
                                                            disabled={isDisabled}
                                                        />
                                                        <span className="ml-2 text-xs font-medium text-gray-700">{option.label}</span>
                                                    </label>
                                                </div>
                                            </td>
                                        ))}
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Progress Grid Section */}
                    <div className="mb-8">
                        <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-4 mb-6 border border-orange-200">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-[#D67C2C] flex items-center justify-center shadow-lg">
                                    <CheckCircle className="w-5 h-5 text-white"/>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900">My Progress Grid</h3>
                                    <p className="text-sm text-gray-600">Select statements that are true based on your performance</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                <tr className="divide-x divide-gray-200">
                                    <th scope="col"
                                        className="px-4 py-3 text-center text-sm font-medium text-gray-900 bg-pink-100 w-1/3">
                                        Awareness
                                    </th>
                                    <th scope="col"
                                        className="px-4 py-3 text-center text-sm font-medium text-gray-900 bg-green-100 w-1/3">
                                        Sensitivity
                                    </th>
                                    <th scope="col"
                                        className="px-4 py-3 text-center text-sm font-medium text-gray-900 bg-blue-100 w-1/3">
                                        Creativity
                                    </th>
                                </tr>
                                </thead>

                                <tbody>
                                {/* Determine the maximum number of rows needed */}
                                {Array.from({
                                    length: Math.max(
                                        progressGrid.awareness.length,
                                        progressGrid.sensitivity.length,
                                        progressGrid.creativity.length
                                    )
                                }).map((_, rowIndex) => (
                                    <tr key={rowIndex}
                                        className={`divide-x divide-gray-200 ${rowIndex % 2 === 1 ? 'bg-gray-50' : ''}`}>
                                        <td className="px-4 py-2">
                                            {progressGrid.awareness[rowIndex] ? (
                                                <label className="flex items-start">
                                                    <input
                                                        type="checkbox"
                                                        checked={form.awareness_selections.includes(progressGrid.awareness[rowIndex].id)}
                                                        onChange={() => handleGridSelectionChange('awareness', progressGrid.awareness[rowIndex].id)}
                                                        className="form-checkbox h-4 w-4 mt-1 text-indigo-600"
                                                        disabled={isDisabled}
                                                    />
                                                    <span
                                                        className="ml-2 text-xs">{progressGrid.awareness[rowIndex].text}</span>
                                                </label>
                                            ) : <div className="h-6"></div>}
                                        </td>
                                        <td className="px-4 py-2">
                                            {progressGrid.sensitivity[rowIndex] ? (
                                                <label className="flex items-start">
                                                    <input
                                                        type="checkbox"
                                                        checked={form.sensitivity_selections.includes(progressGrid.sensitivity[rowIndex].id)}
                                                        onChange={() => handleGridSelectionChange('sensitivity', progressGrid.sensitivity[rowIndex].id)}
                                                        className="form-checkbox h-4 w-4 mt-1 text-indigo-600"
                                                        disabled={isDisabled}
                                                    />
                                                    <span
                                                        className="ml-2 text-xs">{progressGrid.sensitivity[rowIndex].text}</span>
                                                </label>
                                            ) : <div className="h-6"></div>}
                                        </td>
                                        <td className="px-4 py-2">
                                            {progressGrid.creativity[rowIndex] ? (
                                                <label className="flex items-start">
                                                    <input
                                                        type="checkbox"
                                                        checked={form.creativity_selections.includes(progressGrid.creativity[rowIndex].id)}
                                                        onChange={() => handleGridSelectionChange('creativity', progressGrid.creativity[rowIndex].id)}
                                                        className="form-checkbox h-4 w-4 mt-1 text-indigo-600"
                                                        disabled={isDisabled}
                                                    />
                                                    <span
                                                        className="ml-2 text-xs">{progressGrid.creativity[rowIndex].text}</span>
                                                </label>
                                            ) : <div className="h-6"></div>}
                                        </td>
                                    </tr>
                                ))}

                                {/* Teacher Use Only Row */}
                                <tr className="divide-x divide-gray-200">
                                    <td colSpan="3" className="px-4 py-2 text-sm font-medium text-gray-700 text-center">
                                        (For Teacher's Use Only)
                                    </td>
                                </tr>
                                <tr className="divide-x divide-gray-200 bg-gray-50">
                                    <td className="px-4 py-2 text-sm">
                                        No. of statements circled for Awareness: <span
                                        className="font-medium">{form.awareness_selections.length}</span>
                                    </td>
                                    <td className="px-4 py-2 text-sm">
                                        No. of statements circled for Sensitivity: <span
                                        className="font-medium">{form.sensitivity_selections.length}</span>
                                    </td>
                                    <td className="px-4 py-2 text-sm">
                                        No. of statements circled for Creativity: <span
                                        className="font-medium">{form.creativity_selections.length}</span>
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* My Learnings Section */}
                    <div className="mb-8">
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 mb-6 border border-green-200">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-green-500 flex items-center justify-center shadow-lg">
                                    <Lightbulb className="w-5 h-5 text-white"/>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900">My Learnings</h3>
                                    <p className="text-sm text-gray-600">Reflect on what you learned and areas for improvement</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                            <div className="space-y-6">
                                <div>
                                    <label htmlFor="insights" className="block text-sm font-medium text-gray-700 mb-2">
                                        By doing this activity, I learnt
                                    </label>
                                    <textarea
                                        id="insights"
                                        rows="4"
                                        className="shadow-sm focus:ring-2 focus:ring-[#D67C2C] focus:border-[#D67C2C] block w-full sm:text-sm border-gray-300 rounded-xl"
                                        value={form.insights}
                                        onChange={(e) => handleTextChange('insights', e.target.value)}
                                        disabled={isDisabled}
                                        placeholder="Use this space to write your reflections/insights from the activity"
                                    ></textarea>
                                    <p className="mt-2 text-xs text-gray-500 italic">
                                        Use this space to write your reflections/insights from the activity
                                    </p>
                                </div>

                                <div>
                                    <label htmlFor="most_interesting"
                                           className="block text-sm font-medium text-gray-700 mb-2">
                                        The most interesting thing about this activity was
                                    </label>
                                    <textarea
                                        id="most_interesting"
                                        rows="3"
                                        className="shadow-sm focus:ring-2 focus:ring-[#D67C2C] focus:border-[#D67C2C] block w-full sm:text-sm border-gray-300 rounded-xl"
                                        value={form.most_interesting}
                                        onChange={(e) => handleTextChange('most_interesting', e.target.value)}
                                        disabled={isDisabled}
                                        placeholder="Describe what you found most interesting..."
                                    ></textarea>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label htmlFor="need_practice"
                                               className="block text-sm font-medium text-gray-700 mb-2">
                                            I need practice on
                                        </label>
                                        <input
                                            type="text"
                                            id="need_practice"
                                            className="shadow-sm focus:ring-2 focus:ring-[#D67C2C] focus:border-[#D67C2C] block w-full sm:text-sm border-gray-300 rounded-xl"
                                            value={form.need_practice}
                                            onChange={(e) => handleTextChange('need_practice', e.target.value)}
                                            disabled={isDisabled}
                                            placeholder="What skills or concepts need more practice?"
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="need_help" className="block text-sm font-medium text-gray-700 mb-2">
                                            I need help with
                                        </label>
                                        <input
                                            type="text"
                                            id="need_help"
                                            className="shadow-sm focus:ring-2 focus:ring-[#D67C2C] focus:border-[#D67C2C] block w-full sm:text-sm border-gray-300 rounded-xl"
                                            value={form.need_help}
                                            onChange={(e) => handleTextChange('need_help', e.target.value)}
                                            disabled={isDisabled}
                                            placeholder="What do you need additional support with?"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="flex justify-center mt-8 pt-6 border-t border-gray-200">
                        {isSubmitted ? (
                            <div className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-xl shadow-lg text-white bg-green-600">
                                <CheckCircle size={20} className="mr-3"/>
                                Assessment Submitted ✓
                            </div>
                        ) : (
                            <button
                                type="submit"
                                className={`inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-xl shadow-lg text-white transition-all hover:scale-105 ${
                                    loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#D67C2C] hover:bg-[#c06a25] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#D67C2C]'
                                }`}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Submitting...
                                    </>
                                ) : (
                                    <>
                                        <Save size={20} className="mr-3"/>
                                        Submit Self Assessment
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};

export default MiddleStageSelfAssessment;